import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react';
import { AuthError, type Account, type ConsentEmailResult, type RegistrationInput } from '@yuan-math/auth';
import { authConfigured, authProvider } from './auth.ts';
import { isAdmin as resolveIsAdmin } from './admin.ts';

/**
 * Account state for the UI.
 *
 * Deliberately thin, exactly like `LearnerContext`: it holds the current
 * account, exposes the flows, and contains no rules. Validation lives in
 * `@yuan-math/auth`, and credential handling lives in Supabase.
 *
 * Note what is *not* here: no password is ever stored in state, and no token is
 * put in `localStorage` by us — the Supabase client owns session persistence
 * and refresh.
 */

type AuthContextValue = {
  account: Account | null;
  /** False until the stored session has been checked. */
  ready: boolean;
  /** True when this deployment has Supabase credentials. */
  configured: boolean;
  signedIn: boolean;
  /** True for a child account whose guardian has not yet consented (§29). */
  awaitingConsent: boolean;
  /**
   * Whether this account may administer the platform; null while unknown.
   *
   * Answered by the database, never by anything the client holds, and it
   * decides what to *render* and nothing else — the boundary is the row-level
   * security in `database/admin-schema.sql`. A modified bundle that forced this
   * to true would be shown an admin page with no data in it.
   *
   * Null rather than false while the question is in flight, so the admin page
   * can wait instead of flashing "this page is not for you" at an
   * administrator on every reload.
   */
  isAdmin: boolean | null;

  register(input: RegistrationInput): Promise<{ needsActivation: boolean }>;
  activate(code: string): Promise<void>;
  resendActivation(email: string): Promise<void>;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  deleteAccount(): Promise<void>;
  linkProfile(profileId: string): Promise<void>;
  /** Records a guardian's permission from a signed link (§29). */
  grantConsent(token: string): Promise<void>;
  /** Sends a reset link. Never reveals whether the address is registered. */
  requestPasswordReset(email: string): Promise<void>;
  /** Completes a reset and signs the student in. */
  resetPassword(code: string, newPassword: string): Promise<void>;
  /** Re-sends the guardian's email, and says why if it did not go. */
  requestGuardianConsent(): Promise<ConsentEmailResult>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Asks the server to email the guardian, when there is anything to ask about.
 *
 * Fire and forget on purpose: a missing mail server must never block a lesson,
 * and the account page shows the state and offers a retry. The provider does
 * not reject, but the `catch` covers a provider that does.
 */
function requestGuardianConsentFor(provider: NonNullable<typeof authProvider>, account: Account): void {
  if (account.kind !== 'child' || account.consentGrantedAt !== undefined) return;
  if (!provider.requestGuardianConsent) return;
  void provider.requestGuardianConsent().catch(() => undefined);
}

export function AuthProviderComponent({ children }: { children: ReactNode }): JSX.Element {
  const [account, setAccount] = useState<Account | null>(null);
  const [ready, setReady] = useState(false);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  /**
   * Whether the session subscription below has already said who is here.
   *
   * It and the restore below ask the same client the same question, and the
   * restore is a network round trip that can lose the race and answer with a
   * stale `null`. That null then overwrites a perfectly good session, and the
   * signed-in student is shown the visitor header and a sign-up form on
   * `/register` — with nothing further to correct it until the next auth event.
   * The subscription is the client's own live state, so it wins.
   */
  const announced = useRef(false);

  // Restore any existing session on first mount. A failure here is not an
  // error state — it simply means nobody is signed in.
  useEffect(() => {
    let cancelled = false;
    const restore = async (): Promise<void> => {
      if (!authProvider) {
        setReady(true);
        return;
      }
      try {
        const existing = await authProvider.resolveSession('', Date.now());
        if (!cancelled && !announced.current) setAccount(existing);
      } catch {
        if (!cancelled && !announced.current) setAccount(null);
      } finally {
        if (!cancelled) setReady(true);
      }
    };
    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * Follow the session, not just the sign-in button.
   *
   * An activation link signs somebody in without anything here being called:
   * the credential is in the URL, the client consumes it, and a context that
   * only asked once at mount goes on believing nobody is there. The visible
   * result is "Your account is active." underneath a header offering to sign
   * you in, and a "Start learning" button that lands on the sign-up form.
   *
   * The same subscription covers a token refresh and another tab signing out.
   */
  useEffect(() => {
    if (!authProvider?.onSessionChange) return;
    return authProvider.onSessionChange((next) => {
      announced.current = true;
      setAccount(next);
      setReady(true);
    });
  }, []);

  /*
   * Ask the database whether this account administers the platform.
   *
   * Re-asked whenever the account changes, and settled to a definite `false`
   * for a signed-out visitor without a round trip — there is nothing to ask
   * about. A failure resolves false: an unreachable server is not a reason to
   * show somebody an admin link that would not work anyway.
   */
  useEffect(() => {
    if (!account) {
      setIsAdmin(false);
      return;
    }
    let cancelled = false;
    setIsAdmin(null);
    void resolveIsAdmin().then((result) => {
      if (!cancelled) setIsAdmin(result);
    });
    return () => {
      cancelled = true;
    };
  }, [account]);

  const requireProvider = useCallback((): NonNullable<typeof authProvider> => {
    if (!authProvider) {
      throw new AuthError('not-supported', 'Accounts are not available on this deployment yet.');
    }
    return authProvider;
  }, []);

  const register = useCallback(
    async (input: RegistrationInput) => {
      const provider = requireProvider();
      const result = await provider.register(input, Date.now());

      // The account exists but cannot sign in until the email is confirmed.
      const needsActivation = result.account.status === 'pending';

      /*
       * A child account needs a guardian's permission as well as activation
       * (§29) — but asking for the email here only works when registering
       * *left us signed in*.
       *
       * The function that sends it authenticates the learner, which is what
       * stops it being a spam relay, and on a project that confirms email
       * `signUp` returns a user and no session at all. Every child's guardian
       * mail was therefore requested with the anon key, answered 401, and
       * swallowed by the `catch` below — which is why no parent received one.
       * The two moments that do have a session are activation and sign-in, and
       * that is where it is asked for now.
       */
      if (!needsActivation) void requestGuardianConsentFor(provider, result.account);

      return { needsActivation };
    },
    [requireProvider],
  );

  const activate = useCallback(
    async (code: string) => {
      const provider = requireProvider();
      setAccount(await provider.activate(code, Date.now()));
    },
    [requireProvider],
  );

  const resendActivation = useCallback(
    async (email: string) => {
      await requireProvider().resendActivation(email, Date.now());
    },
    [requireProvider],
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      const provider = requireProvider();
      const { account: next } = await provider.signIn(email, password, Date.now());
      setAccount(next);

      // A child whose guardian has not answered is waiting on an email that may
      // never have arrived. A deliberate sign-in is both a moment there is
      // certainly a session to send it with and a sign somebody is waiting.
      void requestGuardianConsentFor(provider, next);
    },
    [requireProvider],
  );

  const signOut = useCallback(async () => {
    if (authProvider && account) {
      await authProvider.signOut({ accountId: account.id, token: '', issuedAt: 0, expiresAt: 0 });
    }
    setAccount(null);
  }, [account]);

  const deleteAccount = useCallback(async () => {
    if (!account) return;
    await requireProvider().deleteAccount(account.id);
    setAccount(null);
  }, [account, requireProvider]);

  const linkProfile = useCallback(
    async (profileId: string) => {
      if (!account) return;
      setAccount(await requireProvider().attachProfile(account.id, profileId));
    },
    [account, requireProvider],
  );

  const grantConsent = useCallback(
    async (token: string) => {
      setAccount(await requireProvider().grantConsent(token, Date.now()));
    },
    [requireProvider],
  );

  const requestGuardianConsent = useCallback(async (): Promise<ConsentEmailResult> => {
    const provider = authProvider;
    if (!provider?.requestGuardianConsent) {
      return {
        sent: false,
        reason: 'not-configured',
        message: 'Email is not switched on for this deployment, so we could not send it.',
      };
    }
    // The provider does not reject, but a thrown error here would leave the
    // learner staring at a button that did nothing at all.
    try {
      return await provider.requestGuardianConsent();
    } catch {
      return {
        sent: false,
        reason: 'unknown',
        message: 'We could not send it just now. Please try again in a few minutes.',
      };
    }
  }, []);

  const requestPasswordReset = useCallback(
    async (email: string) => {
      await requireProvider().requestPasswordReset(email);
    },
    [requireProvider],
  );

  const resetPassword = useCallback(
    async (code: string, newPassword: string) => {
      setAccount(await requireProvider().resetPassword(code, newPassword));
    },
    [requireProvider],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      account,
      ready,
      configured: authConfigured,
      signedIn: account !== null && account.status === 'active',
      awaitingConsent: account?.kind === 'child' && account.consentGrantedAt === undefined,
      isAdmin,
      register,
      activate,
      resendActivation,
      signIn,
      signOut,
      deleteAccount,
      linkProfile,
      grantConsent,
      requestGuardianConsent,
      requestPasswordReset,
      resetPassword,
    }),
    [account, ready, isAdmin, register, activate, resendActivation, signIn, signOut, deleteAccount, linkProfile, grantConsent, requestGuardianConsent, requestPasswordReset, resetPassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
